// src/components/layout/Layout.jsx
import React, { useState } from 'react';
import { usePersistedState } from '../shared/UI';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { orgAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  LayoutDashboard, Briefcase, Users, Settings, LogOut,
  Menu, Building2, ChevronDown, ChevronRight, ChevronLeft,
  MapPin, Image, FileText, Plus, Pencil, Search, ArrowLeftRight, UserCheck,
  Clock, SlidersHorizontal, User, AlignLeft, GraduationCap, Zap, Globe, BookOpen,
} from 'lucide-react';
import { clearLoginDefault } from '../../pages/DestinationSelector';

// ── Service keys ──────────────────────────────────────────
const SERVICES = { RECRUITMENT: 'RECRUITMENT' };

// ── Nav definitions ───────────────────────────────────────
const employerProfileSubNav = [
  { to: '/employer/profile/overview',   label: 'Overview',      icon: FileText  },
  { to: '/employer/profile/workplaces', label: 'Workplaces',    icon: MapPin    },
  { to: '/employer/profile/contacts',   label: 'Contacts',      icon: Users     },
  { to: '/employer/profile/jobs',       label: 'Position Templates', icon: Briefcase },
  { to: '/employer/profile/media',      label: 'Content Library', icon: Image     },
];

const employerRecruitmentSubNav = [
  { to: '/employer/job-ads',    label: 'Job Ads',    icon: FileText },
  { to: '/employer/candidates', label: 'Candidates', icon: UserCheck },
];

const employerSettingsNav = [
  { to: '/employer/settings/members', label: 'Members', icon: Users },
];

const seekerRecruitmentNav = [
  { to: '/seeker/availability',             label: 'Availability',     icon: Clock              },
  { to: '/seeker/profile',                  label: 'CVs', exact: true, icon: FileText           },
  { to: '/seeker/profile/work-preferences', label: 'Work Preferences', icon: SlidersHorizontal },
];

const seekerProfileNav = [
  { to: '/seeker/profile/personal',       label: 'ID & Contacts',        icon: User           },
  { to: '/seeker/profile/summary',        label: 'Professional Summary', icon: AlignLeft      },
  { to: '/seeker/profile/qualifications', label: 'Education',            icon: GraduationCap  },
  { to: '/seeker/profile/experience',     label: 'Work Experience',      icon: Briefcase      },
  { to: '/seeker/profile/skills',         label: 'Skills',               icon: Zap            },
  { to: '/seeker/profile/languages',      label: 'Languages',            icon: Globe          },
  { to: '/seeker/profile/references',     label: 'References',           icon: BookOpen       },
];

const staticNavItems = {
  JOB_SEEKER: [
    { to: '/seeker',                  label: 'Dashboard',      icon: LayoutDashboard, exact: true },
    { to: '/seeker/profile/personal', label: 'My Profile',     icon: UserCheck,       sub: seekerProfileNav },
    { to: '/seeker/profile',          label: 'My Recruitment', icon: Briefcase,       sub: seekerRecruitmentNav },
    { to: '/seeker/jobs',             label: 'Browse Jobs',    icon: Search },
  ],
  ADMIN: [
    { to: '/admin',        label: 'Overview', icon: LayoutDashboard, exact: true },
    { to: '/admin/config', label: 'Config',   icon: Settings },
  ],
};

const buildEmployerNav = () => [
  { to: '/employer',             label: 'Dashboard',   icon: LayoutDashboard, exact: true },
  { to: '/employer/profile',     label: 'Profile',     icon: Building2,       sub: employerProfileSubNav },
  { to: '/employer/recruitment', label: 'Recruitment', icon: Search,          sub: employerRecruitmentSubNav },
  { to: '/employer/settings',    label: 'Settings',    icon: Settings,        sub: employerSettingsNav },
];

// ── Org Switcher ──────────────────────────────────────────
const OrgSwitcher = ({ collapsed }) => {
  const { orgs, activeOrg, activeProfile, switchProfile, refreshOrgs, isOrgOwner } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await orgAPI.createProfile(activeOrg.id, { profileName: name });
      await refreshOrgs();
      setCreating(false);
      setNewName('');
      toast.success(`"${name}" profile created`);
    } catch {
      // error toast already shown by Axios interceptor
    } finally {
      setSaving(false);
    }
  };

  if (!orgs?.length) return null;

  // ── Collapsed: just the org initial avatar ──
  if (collapsed) {
    const tooltip = [activeOrg?.name, activeProfile?.profileName].filter(Boolean).join(' — ');
    return (
      <div style={{
        padding: '8px 0', borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'center',
      }}>
        <div
          title={tooltip || 'Organisation'}
          style={{
            width: 32, height: 32, borderRadius: 6, background: '#3D80CC',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <span style={{ color: 'white', fontSize: '12px', fontWeight: 700 }}>
            {activeOrg?.name?.[0]?.toUpperCase() || 'O'}
          </span>
        </div>
      </div>
    );
  }

  // ── Expanded ──
  const profiles = activeOrg?.groupProfiles || [];

  return (
    <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', marginBottom: '4px' }}>

      {/* Org name row with edit icon */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '6px 8px', borderRadius: '8px',
        background: 'var(--off-white)', border: '1px solid var(--border)',
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: '6px',
          background: '#3D80CC', display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          <span style={{ color: 'white', fontSize: '11px', fontWeight: 700 }}>
            {activeOrg?.name?.[0]?.toUpperCase() || 'O'}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '12px', fontWeight: 600, color: 'var(--text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {activeOrg?.name || 'Select Organisation'}
          </div>
          {activeOrg?.myOrgRole && (
            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              {activeOrg.myOrgRole}
            </div>
          )}
        </div>
        <Link
          to="/employer/org"
          title="Edit organisation"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '4px', borderRadius: '5px', color: 'var(--text-muted)',
            textDecoration: 'none', flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#3D80CC'; e.currentTarget.style.background = 'rgba(61,128,204,0.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
        >
          <Pencil size={12} />
        </Link>
      </div>

      {/* Profile dropdown */}
      {profiles.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div
            onClick={() => setProfileOpen(!profileOpen)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '5px 8px', borderRadius: '6px', cursor: 'pointer',
              border: '1px solid var(--border)', background: 'white',
            }}
          >
            <div style={{
              fontSize: '11px', fontWeight: 500, color: '#3D80CC',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {activeProfile?.profileName || 'Select Profile'}
            </div>
            <ChevronDown size={12} style={{
              color: 'var(--text-muted)', flexShrink: 0,
              transform: profileOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s',
            }} />
          </div>

          {profileOpen && (
            <div style={{
              marginTop: 2, background: 'white', border: '1px solid var(--border)',
              borderRadius: 6, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}>
              {profiles.map((profile) => (
                <div
                  key={profile.id}
                  onClick={() => { switchProfile(profile); setProfileOpen(false); }}
                  style={{
                    padding: '6px 10px', cursor: 'pointer', fontSize: '11px',
                    color: profile.id === activeProfile?.id ? '#3D80CC' : 'var(--text-mid)',
                    background: profile.id === activeProfile?.id ? '#F0F7FF' : 'transparent',
                    fontWeight: profile.id === activeProfile?.id ? 600 : 400,
                  }}
                  onMouseEnter={e => { if (profile.id !== activeProfile?.id) e.currentTarget.style.background = '#F8F9FA'; }}
                  onMouseLeave={e => { if (profile.id !== activeProfile?.id) e.currentTarget.style.background = 'transparent'; }}
                >
                  {profile.profileName}
                </div>
              ))}

              {isOrgOwner && (
                creating ? (
                  <form onSubmit={handleCreateProfile} style={{ padding: '6px 10px', borderTop: '1px dashed var(--border)', background: '#FAFAFA' }}>
                    <input
                      autoFocus
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                      placeholder="Profile name…"
                      disabled={saving}
                      style={{
                        width: '100%', fontSize: '11px', padding: '4px 8px',
                        border: '1px solid var(--border)', borderRadius: 4,
                        outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                        marginBottom: 4,
                      }}
                      onKeyDown={e => { if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
                    />
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button type="submit" disabled={saving || !newName.trim()} style={{
                        flex: 1, fontSize: '10px', fontWeight: 600, padding: '3px 0',
                        background: '#3D80CC', color: 'white', border: 'none',
                        borderRadius: 3, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
                      }}>
                        {saving ? 'Creating…' : 'Create'}
                      </button>
                      <button type="button" onClick={() => { setCreating(false); setNewName(''); }} style={{
                        fontSize: '10px', fontWeight: 600, padding: '3px 8px',
                        background: '#eee', color: 'var(--text-mid)', border: 'none',
                        borderRadius: 3, cursor: 'pointer',
                      }}>
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div
                    onClick={() => setCreating(true)}
                    style={{
                      padding: '6px 10px', cursor: 'pointer', fontSize: '11px',
                      color: '#3D80CC', background: '#FAFAFA',
                      display: 'flex', alignItems: 'center', gap: 4,
                      borderTop: '1px dashed var(--border)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F0F7FF'}
                    onMouseLeave={e => e.currentTarget.style.background = '#FAFAFA'}
                  >
                    <Plus size={10} /> Create New Profile
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Nav Item ──────────────────────────────────────────────
const NavItem = ({ item, location, onNavigate, collapsed }) => {
  const isActive = item.exact
    ? location.pathname === item.to
    : location.pathname === item.to || location.pathname.startsWith(item.to + '/');

  const hasActiveSub = item.sub?.some((s) =>
    s.exact
      ? location.pathname === s.to
      : location.pathname === s.to || location.pathname.startsWith(s.to + '/')
  );

  const isHighlighted = isActive || hasActiveSub;
  const [subOpen, setSubOpen] = usePersistedState(`tikhet:nav:${item.label}:open`, isHighlighted);
  const Icon = item.icon;

  // ── Collapsed: icon-only with tooltip ──
  if (collapsed) {
    if (item.sub) {
      return (
        <div style={{ borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
          <button
            onClick={() => setSubOpen(v => !v)}
            title={item.label}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '9px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
              color: isHighlighted ? '#3D80CC' : 'var(--text-muted)',
              background: isHighlighted ? 'rgba(61,128,204,0.08)' : subOpen ? 'rgba(61,128,204,0.04)' : 'transparent',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { if (!isHighlighted) e.currentTarget.style.background = 'var(--off-white)'; }}
            onMouseLeave={e => { if (!isHighlighted) e.currentTarget.style.background = subOpen ? 'rgba(61,128,204,0.04)' : 'transparent'; }}
          >
            {Icon && <Icon size={18} />}
          </button>
          {subOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, paddingBottom: 2 }}>
              {item.sub.map(s => {
                const subActive = s.exact
                  ? location.pathname === s.to
                  : location.pathname === s.to || location.pathname.startsWith(s.to + '/');
                const SubIcon = s.icon;
                return (
                  <Link
                    key={s.to}
                    to={s.to}
                    onClick={onNavigate}
                    title={s.label}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '7px 0', borderRadius: 6, textDecoration: 'none',
                      color: subActive ? '#3D80CC' : 'var(--text-muted)',
                      background: subActive ? 'rgba(61,128,204,0.10)' : 'transparent',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (!subActive) e.currentTarget.style.background = 'var(--off-white)'; }}
                    onMouseLeave={e => { if (!subActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {SubIcon ? <SubIcon size={14} /> : <span style={{ fontSize: 18, lineHeight: 1, color: 'inherit' }}>·</span>}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    return (
      <Link
        to={item.to}
        onClick={onNavigate}
        title={item.label}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '9px 0', borderRadius: 8, textDecoration: 'none',
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          color: isHighlighted ? '#3D80CC' : 'var(--text-muted)',
          background: isHighlighted ? 'rgba(61,128,204,0.08)' : 'transparent',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { if (!isHighlighted) e.currentTarget.style.background = 'var(--off-white)'; }}
        onMouseLeave={e => { if (!isHighlighted) e.currentTarget.style.background = 'transparent'; }}
      >
        {Icon && <Icon size={18} />}
      </Link>
    );
  }

  // ── Expanded ──
  return (
    <div style={{ borderRadius: 8, overflow: 'hidden', background: 'rgba(0,0,0,0.025)' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid rgba(0,0,0,0.07)',
        }}
      >
        <Link
          to={item.sub ? '#' : item.to}
          onClick={(e) => { if (item.sub) { e.preventDefault(); setSubOpen(!subOpen); } else { onNavigate(); } }}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 10px', textDecoration: 'none',
            fontSize: 13, fontWeight: isHighlighted ? 600 : 400,
            color: isHighlighted ? '#3D80CC' : 'var(--text-mid)',
            background: isHighlighted && !item.sub ? 'rgba(61,128,204,0.08)' : 'transparent',
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => { if (!isHighlighted) e.currentTarget.style.background = 'var(--off-white)'; }}
          onMouseLeave={(e) => { if (!isHighlighted) e.currentTarget.style.background = 'transparent'; }}
        >
          {Icon && <Icon size={15} style={{ flexShrink: 0, color: isHighlighted ? '#3D80CC' : 'var(--text-muted)' }} />}
          {item.label}
        </Link>
        {item.sub && (
          <button
            onClick={() => setSubOpen(!subOpen)}
            style={{
              padding: '8px 8px', background: 'none', border: 'none',
              cursor: 'pointer', color: 'var(--text-muted)', display: 'flex',
            }}
          >
            <ChevronRight size={13} style={{
              transform: subOpen ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.2s',
            }} />
          </button>
        )}
      </div>

      {item.sub && subOpen && (
        <div style={{ marginLeft: 24, marginTop: 2, marginBottom: 4 }}>
          {item.sub.map((s) => {
            const subActive = s.exact
              ? location.pathname === s.to
              : location.pathname === s.to || location.pathname.startsWith(s.to + '/');
            const SubIcon = s.icon;
            return (
              <Link
                key={s.to}
                to={s.to}
                onClick={onNavigate}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px', borderRadius: 6, textDecoration: 'none',
                  fontSize: 12, fontWeight: subActive ? 600 : 400,
                  color: subActive ? '#3D80CC' : 'var(--text-muted)',
                  background: subActive ? 'rgba(61,128,204,0.08)' : 'transparent',
                  marginBottom: 1,
                }}
                onMouseEnter={(e) => { if (!subActive) e.currentTarget.style.background = 'var(--off-white)'; }}
                onMouseLeave={(e) => { if (!subActive) e.currentTarget.style.background = 'transparent'; }}
              >
                {SubIcon && <SubIcon size={12} style={{ flexShrink: 0 }} />}
                {s.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Sidebar edge chevron tab ──────────────────────────────
const SidebarChevronTab = ({ collapsed, onToggle }) => (
  <button
    onClick={onToggle}
    title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
    style={{
      position: 'absolute', right: -13, top: '50%',
      transform: 'translateY(-50%)',
      width: 26, height: 52, padding: 0, zIndex: 10,
      background: 'white', cursor: 'pointer',
      border: '1px solid var(--border)', borderLeft: 'none',
      borderRadius: '0 10px 10px 0',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--text-muted)',
      boxShadow: '2px 0 6px rgba(0,0,0,0.06)',
      transition: 'color 0.15s, background 0.15s',
    }}
    onMouseEnter={e => { e.currentTarget.style.color = '#3D80CC'; e.currentTarget.style.background = '#F0F7FF'; }}
    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'white'; }}
  >
    {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
  </button>
);

// ── Sidebar Content ───────────────────────────────────────
const SidebarContent = ({ items, location, user, roleLabel, navContext, onLogout, onNavigate, collapsed }) => (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

    {/* ── Logo ── */}
    <div style={{
      padding: collapsed ? '16px 8px 12px' : '16px 16px 14px',
      borderBottom: '1px solid var(--border)',
      display: 'flex', alignItems: 'center',
      justifyContent: collapsed ? 'center' : 'flex-start',
      gap: 10,
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 8, flexShrink: 0,
        background: 'linear-gradient(135deg, #3D80CC, #8B1A2F)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ color: 'white', fontWeight: 800, fontSize: 14 }}>T</span>
      </div>
      {!collapsed && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)', lineHeight: 1 }}>Tikhet</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{roleLabel}</div>
        </div>
      )}
    </div>

    {/* Org switcher — visible on employer routes when user has org access */}
    {navContext === 'EMPLOYER' && <OrgSwitcher collapsed={collapsed} />}

    {/* Nav */}
    <nav style={{ flex: 1, padding: collapsed ? '8px 6px' : '8px 12px', overflowY: 'auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((item) => (
          <NavItem
            key={item.to}
            item={item}
            location={location}
            onNavigate={onNavigate}
            collapsed={collapsed}
          />
        ))}
      </div>
    </nav>

    {/* User / logout */}
    <div style={{ padding: collapsed ? '10px 6px' : '12px 16px', borderTop: '1px solid var(--border)' }}>
      {collapsed ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <Link
            to="/select-destination"
            onClick={clearLoginDefault}
            title="Switch workspace"
            style={{
              padding: '6px', borderRadius: 6, border: 'none', background: 'none',
              cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', textDecoration: 'none',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#3D80CC'; e.currentTarget.style.background = 'rgba(61,128,204,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'none'; }}
          >
            <ArrowLeftRight size={15} />
          </Link>
          <button
            onClick={onLogout}
            title="Logout"
            style={{
              padding: '6px', borderRadius: 6, border: 'none', background: 'none',
              cursor: 'pointer', color: 'var(--text-muted)', display: 'flex',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#e55'; e.currentTarget.style.background = '#FEF2F2'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'none'; }}
          >
            <LogOut size={15} />
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.email}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
              {user?.role?.replace('_', ' ')}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
            <Link
              to="/select-destination"
              onClick={clearLoginDefault}
              title="Switch workspace"
              style={{
                padding: '6px', borderRadius: 6, border: 'none', background: 'none',
                cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', textDecoration: 'none',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#3D80CC'; e.currentTarget.style.background = 'rgba(61,128,204,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'none'; }}
            >
              <ArrowLeftRight size={15} />
            </Link>
            <button
              onClick={onLogout}
              title="Logout"
              style={{
                padding: '6px', borderRadius: 6, border: 'none', background: 'none',
                cursor: 'pointer', color: 'var(--text-muted)', display: 'flex',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#e55'; e.currentTarget.style.background = '#FEF2F2'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'none'; }}
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
);

// ── Main Layout ───────────────────────────────────────────
export const Layout = ({ children }) => {
  const { user, logout, orgs, activeProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = usePersistedState('tikhet:sidebar:collapsed', false);

  // Derive nav context from route (not user.role) so employer features
  // work for any user with org access, regardless of UserRole.
  const navContext = location.pathname.startsWith('/employer') && orgs?.length > 0
    ? 'EMPLOYER'
    : location.pathname.startsWith('/admin')
    ? 'ADMIN'
    : 'JOB_SEEKER';

  const items = navContext === 'EMPLOYER'
    ? buildEmployerNav()
    : (staticNavItems[navContext] || []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const roleLabel = {
    EMPLOYER:   'Employer Portal',
    JOB_SEEKER: 'Job Seeker Portal',
    ADMIN:      'Admin Portal',
  }[navContext] || '';

  const sidebarProps = {
    items, location, user, roleLabel, navContext,
    onLogout: handleLogout,
    onNavigate: () => setMobileOpen(false),
    collapsed,
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: 'var(--bg)', overflow: 'hidden' }}>

      {/* Desktop sidebar */}
      <div style={{ position: 'relative', flexShrink: 0 }} className="hidden-mobile">
        <aside style={{
          width: collapsed ? 52 : 220,
          height: '100%', background: 'white',
          borderRight: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          transition: 'width 0.2s ease',
        }}>
          <SidebarContent {...sidebarProps} />
        </aside>
        <SidebarChevronTab collapsed={collapsed} onToggle={() => setCollapsed(v => !v)} />
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            zIndex: 40, display: 'none',
          }}
          className="show-mobile"
        />
      )}

      {/* Mobile sidebar drawer */}
      <aside style={{
        position: 'fixed', top: 0, left: 0, height: '100vh', width: 260,
        background: 'white', borderRight: '1px solid var(--border)',
        zIndex: 50, transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.25s ease', display: 'none',
      }} className="show-mobile">
        {/* Mobile always shows expanded sidebar */}
        <SidebarContent {...sidebarProps} collapsed={false} />
      </aside>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Mobile topbar */}
        <header style={{
          padding: '12px 16px', background: 'white', borderBottom: '1px solid var(--border)',
          display: 'none', alignItems: 'center', justifyContent: 'space-between',
        }} className="show-mobile">
          <button onClick={() => setMobileOpen(true)}
            style={{ padding: 6, border: 'none', background: 'none', cursor: 'pointer' }}>
            <Menu size={20} style={{ color: 'var(--text)' }} />
          </button>
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>Tikhet</span>
          <div style={{ width: 32 }} />
        </header>

        {/* Page content */}
        <main style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {children}
        </main>
      </div>
    </div>
  );
};
